<script setup>
  import { computed } from 'vue';
  import { useI18n } from 'vue-i18n';
  import { useSpotStore } from "@/stores/SpotStore";
  import useSpotGlobals from '@/composables/useSpotGlobals';
  import Icon from "@/components/icons/Icon.vue";

  const { $ALLOWED_STATION_CAPABILITIES } = useSpotGlobals();

  const emit = defineEmits(['data-item:click']);

  const props = defineProps({
    upgrade: {
      type: Array,
      required: true
    },
    allowedDataValues: {
      type: Array,
      default: () => []
    },
    tagList: {
      type: Boolean,
      default: false
    },
    useButton: {
      type: Boolean,
      default: false
    },
    spotDataObject: {
      type: Object,
      required: true
    }
  });

  const spotStore = useSpotStore();
  const { t } = useI18n();

  // Also used for display order
  const defaultAllowedDataValues = [
    'windGust',
    'airTemp',
    'surfaceTemp',
    'relativeHumidity',
    'airPressure',
    'waterTemp',
    'waterSkinTemp',
    'swell',
    'waterLevel',
    'stationElevation',
  ];

  const allowedDataValues = computed(() => {
    const dataValues = props.allowedDataValues.length ? props.allowedDataValues : defaultAllowedDataValues;

    if (props.spotDataObject.isNowcast) {
      const windGustIndex = dataValues.indexOf('windGust');

      if (windGustIndex > -1) {
        dataValues.splice(windGustIndex, 1);
      }
    }   

    return dataValues;
  });

  const currentConditionsDisplayData = computed(() => {
    const displayData = [];

    if (isAllowed('windGust')) displayData[getDataPosition('windGust')] = [t('containers.currentConditions.labels.windGust'), 'windGust', getGustingDisplayString(props.spotDataObject.formattedData.windGust, props.spotDataObject.stationApiDataValues.lull), 'gusts'];
    if (isAllowed('airTemp')) displayData[getDataPosition('airTemp')] = [t('containers.currentConditions.labels.airTemp'), 'airTemp', props.spotDataObject.formattedData.airTemp, 'thermometer'];
    if (isAllowed('airPressure')) displayData[getDataPosition('airPressure')] = [t('containers.currentConditions.labels.airPressure'), 'airPressure', props.spotDataObject.formattedData.airPressure, 'pressure-heavy'];

    if (isAllowed('surfaceTemp') && $ALLOWED_STATION_CAPABILITIES['surfaceTemp'] && props.spotDataObject.stationApiDataValues.surface_temp) {
      displayData[getDataPosition('surfaceTemp')] = [t('containers.currentConditions.labels.surfaceTemp'), 'surfaceTemp', props.spotDataObject.formattedData.surfaceTemp, 'surface-temp'];
    }

    if (isAllowed('relativeHumidity') && props.spotDataObject.stationApiDataValues.humidity) {
      displayData[getDataPosition('relativeHumidity')] = [t('containers.currentConditions.labels.relativeHumidity'), 'relativeHumidity', props.spotDataObject.formattedData.relativeHumidity, 'humidity-heavy'];
    }    
    
    if (isAllowed('waterTemp') && props.spotDataObject.stationApiDataValues.wtemp) {
      displayData[getDataPosition('waterTemp')] = [t('containers.currentConditions.labels.waterTemp'), 'waterTemp', props.spotDataObject.formattedData.waterTemp, 'sst'];
    }
    
    if (isAllowed('swell') && props.spotDataObject.stationApiDataValues.wave_height && props.spotDataObject.stationApiDataValues.wave_height > 0) {
      const formattedWaveHeightData = props.spotDataObject.formattedData.waveHeight;
      const swellFormattedData = props.spotDataObject.stationApiDataValues.wave_period ? `${formattedWaveHeightData} @ ${props.spotDataObject.stationApiDataValues.wave_period} s` : formattedWaveHeightData;

      displayData[getDataPosition('swell')] = [t('containers.currentConditions.labels.swell'), 'swell', swellFormattedData, 'tides'];
    }

    if (isAllowed('waterLevel') && props.spotDataObject.station.water_level && props.spotDataObject.stationApiDataValues.water_level > 0) {
      if (props.spotDataObject.spotId === 165021 || props.spotDataObject.spotId === 165020) {
        displayData[getDataPosition('waterLevel')] = [t('containers.currentConditions.labels.waterLevel'), 'waterLevel', t('common.notAvailable'), 'water-level']
        
        return;
      };

      displayData[getDataPosition('waterLevel')] = [t('containers.currentConditions.labels.waterLevel'), 'waterLevel', props.spotDataObject.formattedData.waterLevel, 'water-level'];
    }

    if (isAllowed('stationElevation') && props.spotDataObject.station.station_elevation && props.spotDataObject.stationApiDataValues.station_elevation > 0) {
      displayData[getDataPosition('stationElevation')] = [t('containers.currentConditions.labels.stationElevation'), 'stationElevation', props.spotDataObject.formattedData.stationElevation, 'info'];
    }
    
    // Remove empty array indexes and return;
    return displayData.filter(data => data);
  });

  const dataItemClickEvent = computed(() => {
    if (!props.useButton) return {};

    return { 'click': handleDataItemClick }
  });

  function handleDataItemClick(event) {
    const fieldName = event.currentTarget.dataset.fieldName;
    const field = props.spotDataObject.apiDataFieldNames[fieldName] || fieldName;

    emit('data-item:click', field);
  }

  function getGustingDisplayString(formattedGustValue, lullValue) {
    if (!props.spotDataObject.stationApiDataValues.gust) return formattedGustValue;

    if (props.spotDataObject.status === 4) return '<span class="jwx-data-value__value">&ndash;&ndash;</span>'

    const lullString = lullValue || '';

    return t('containers.currentConditions.lullToGust', { lull: lullString, gust: formattedGustValue });
  }

  function getDataPosition(dataName) {
    return allowedDataValues.value.indexOf(dataName);
  }  

  function isAllowed(dataName) {
    return allowedDataValues.value.includes(dataName);
  }
</script>

<template>
  <component 
    :is="props.useButton ? 'div' : 'li'"
    class="jwx-current-conditons-data-list jwx-data-list"
    :class="{'jwx-data-list--tags': props.tagList}"
    :data-tag-count="currentConditionsDisplayData.length"
  >
    <component
      :is="props.useButton ? 'button' : 'div'"
      v-for="[label, name, value, icon] in currentConditionsDisplayData" 
      :type="props.useButton ? 'button' : null"
      :key="name" 
      :data-icons="!props.tagList ? 'true' : null"
      :data-field-name="name"
      class="jwx-current-conditons-data-list__item jwx-data-list__line-item"
      v-on="dataItemClickEvent"
    >
      <Icon :name="icon" class="jwx-data-list__icon"/>
      <p class="jwx-data-list__label">{{ label }}</p>

      <p
        v-if="name === 'windGust' && props.upgrade[0]"
        class="jwx-data-list__value"
      >
        <Icon name="lock" class="jwx-data-list__upgrade-icon"/>
      </p>

      <div v-else v-html="value" class="jwx-data-list__value"></div>
    </component>
  </component>
</template>